import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Mountain } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const links = [
  { to: "/retreat", label: "The Retreat" },
  { to: "/why-el-salvador", label: "Why El Salvador" },
  { to: "/founders", label: "Founders" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const solid = scrolled || !isHome;
  const textColor = solid ? "text-[#1A1C1B]" : "text-white";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        solid ? "glass-nav bg-white/80 border-b border-black/5 shadow-sm" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className={`flex items-center gap-2 font-display font-extrabold text-lg tracking-tight ${textColor}`}>
          <Mountain className="w-6 h-6 text-primary" strokeWidth={2.5} />
          <span className="hidden sm:inline">La Vida Is Travel Fit</span>
          <span className="sm:hidden">La Vida</span>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1)}`}
              className={`text-sm font-medium hover:text-primary transition-colors ${textColor}`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <Link
                to={user.role === "admin" ? "/admin" : "/portal"}
                data-testid="nav-portal"
                className={`hidden sm:inline-flex items-center gap-2 text-sm font-medium ${textColor} hover:text-primary`}
              >
                <User className="w-4 h-4" /> {user.role === "admin" ? "Dashboard" : "My Trip"}
              </Link>
              <button
                onClick={logout}
                data-testid="nav-logout"
                className={`hidden sm:inline-flex ${textColor} hover:text-primary`}
                title="Log out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login" className={`hidden sm:inline text-sm font-medium ${textColor} hover:text-primary`}>
              Log in
            </Link>
          )}
          <button
            onClick={() => navigate("/book")}
            data-testid="nav-book-cta"
            className="hidden sm:inline-flex items-center rounded-full bg-primary text-white px-5 h-10 text-sm font-semibold hover:bg-[#B84726] hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
          >
            Book Your Retreat
          </button>
          <button onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" className={`lg:hidden ${textColor}`}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden glass-nav bg-white/95 border-t border-black/5 px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-mobile-${l.to.slice(1)}`} className="text-base font-medium text-[#1A1C1B]">
              {l.label}
            </Link>
          ))}
          <div className="h-px bg-black/10 my-2" />
          {user ? (
            <>
              <Link to={user.role === "admin" ? "/admin" : "/portal"} className="text-base font-medium">
                {user.role === "admin" ? "Dashboard" : "My Trip"}
              </Link>
              <button onClick={logout} className="text-left text-base font-medium text-primary">Log out</button>
            </>
          ) : (
            <Link to="/login" className="text-base font-medium">Log in</Link>
          )}
          <button onClick={() => navigate("/book")} className="rounded-full bg-primary text-white px-5 h-11 text-sm font-semibold">
            Book Your Retreat
          </button>
        </div>
      )}
    </header>
  );
}
